import { createReflection, updateReflection, seedRichContent, type ReflectionSession } from './reflectiveJournal'
import { createDocument, qirtaas } from './qirtaas'
import type { CalendarStudyEvent } from './calendarEventNotes'

const API='https://api.dawwin.al-ilm.net/calendar'

function eventDateOnly(event:CalendarStudyEvent){const raw=event.start?.dateTime||event.start?.date;if(!raw)return new Date().toISOString().slice(0,10);return raw.slice(0,10)}

// Mirrors createCalendarEventNote's create-then-link shape -- a reflection created from a
// calendar event is rich from the start, exactly like one created from Reflective Journal's
// own "+ Add reflection" (ReflectionCapture), reusing the same seedRichContent() shape so
// every entry point produces an identical, structured reflection.
export async function createCalendarEventReflection(event:CalendarStudyEvent,notebookId:string,journalId?:string){
 let documentId=''
 try{
  const created=await createDocument(seedRichContent())
  documentId=created.id
  const reflection=await createReflection({
   notebook_id:notebookId,
   ...(journalId?{journal_id:journalId}:{}),
   session_date:eventDateOnly(event),
   session_title:event.summary?.trim()||'',
   content_document_id:documentId,
  })
  const response=await fetch(`${API}/event-reflections`,{method:'POST',credentials:'include',headers:{'Content-Type':'application/json'},body:JSON.stringify({calendarId:event.calendarId||'',eventId:event.id,reflectionId:reflection.reflection.id})})
  if(!response.ok)throw new Error('calendar_event_reflection_link_failed')
  return reflection.reflection.id
 }catch(error){
  if(documentId)await qirtaas.deleteDocument(documentId).catch(console.error)
  throw error
 }
}

// Lightweight "which of my reflections already have a calendar link" set, for the journal
// home list's linked-session indicator -- no per-row Google Calendar call.
export async function listLinkedReflectionIds():Promise<Set<string>>{
 const response=await fetch(`${API}/event-reflections`,{credentials:'include'})
 if(!response.ok)return new Set()
 const data=await response.json().catch(()=>null) as{reflectionIds?:string[]}|null
 return new Set(data?.reflectionIds||[])
}

// Retrospective linking (point 2/3): attaches an EXISTING reflection to an EXISTING event.
// Title is prefilled only if the reflection doesn't already have one -- never clobbers
// something the user already wrote. Date is always synced to the event's date, since linking
// declares "this reflection is about this session" -- the user can still edit it afterwards
// independently (point 4). Mirrors createCalendarEventReflection's link step; the only new
// part is this prefill PATCH.
export async function linkReflectionToEvent(reflection:Pick<ReflectionSession,'id'|'session_title'|'session_date'>,event:CalendarStudyEvent){
 const response=await fetch(`${API}/event-reflections`,{method:'POST',credentials:'include',headers:{'Content-Type':'application/json'},body:JSON.stringify({calendarId:event.calendarId||'',eventId:event.id,reflectionId:reflection.id})})
 if(!response.ok)throw new Error('calendar_event_reflection_link_failed')
 const prefill:Record<string,string>={session_date:eventDateOnly(event)}
 if(!reflection.session_title.trim()&&event.summary?.trim())prefill.session_title=event.summary.trim()
 await updateReflection(reflection.id,prefill)
}

export async function unlinkReflection(reflectionId:string){
 const response=await fetch(`${API}/event-reflections/${encodeURIComponent(reflectionId)}`,{method:'DELETE',credentials:'include'})
 if(!response.ok&&response.status!==404)throw new Error('calendar_event_reflection_unlink_failed')
}

export type ReflectionSessionContext={
 linked:boolean
 calendarId?:string
 eventId?:string
 documentId?:string
 event:{summary?:string;start?:{dateTime?:string;date?:string};end?:{dateTime?:string;date?:string};htmlLink?:string}|null
}
// Reverse lookup for the reflection's own "Based on: <session>" header context, and any study
// note already linked to the same calendar event. Reuses the existing calendar_event_reflections
// / calendar_event_documents linkage server-side -- see api/src/calendar.ts.
export async function getReflectionSessionContext(reflectionId:string):Promise<ReflectionSessionContext>{
 const response=await fetch(`${API}/event-reflections/${encodeURIComponent(reflectionId)}`,{credentials:'include'})
 if(!response.ok)return{linked:false,event:null}
 return response.json()
}